import { useState, useEffect, useMemo } from "react";
import {
  TrendingUp, TrendingDown, Minus, Loader2, Play,
  BarChart3, AlertTriangle, Lightbulb, ShieldAlert,
  Package, Users, Calendar, ArrowUpRight, ArrowDownRight, FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { downloadExcel } from "@/utils/excelExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Forecasting = () => {
  const [materials, setMaterials] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [filterType, setFilterType] = useState("material");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [horizon, setHorizon] = useState(3);
  const [loading, setLoading] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => { fetchMaterials(); }, []);

  const fetchMaterials = async () => {
    try {
      const resp = await axios.get(`${API}/forecast/materials`);
      setMaterials(resp.data.materials || []);
      setMaterialGroups(resp.data.material_groups || []);
    } catch (err) {
      console.error("Failed to load materials:", err);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const runForecast = async () => {
    const filterValue = filterType === "material" ? selectedMaterial : selectedGroup;
    if (!filterValue) {
      toast.error(`Select a ${filterType === "material" ? "material" : "material group"} first`);
      return;
    }
    setLoading(true);
    try {
      const params = { horizon_months: horizon };
      if (filterType === "material") params.material = selectedMaterial;
      else params.material_group = selectedGroup;

      const resp = await axios.post(`${API}/forecast/demand`, null, {
        params,
        timeout: 60000,
      });
      setResult(resp.data);
      toast.success("Forecast generated successfully");
    } catch (err) {
      toast.error(`Forecast failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const trendIcon = (trend) => {
    if (trend === "increasing") return <TrendingUp className="w-5 h-5 text-emerald-500" />;
    if (trend === "decreasing") return <TrendingDown className="w-5 h-5 text-red-500" />;
    return <Minus className="w-5 h-5 text-muted-foreground" />;
  };

  const confidenceBadge = (conf) => {
    const map = { high: "bg-emerald-500/10 text-emerald-600", medium: "bg-amber-500/10 text-amber-600", low: "bg-red-500/10 text-red-600" };
    return <Badge className={map[conf] || map.low}>{conf}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Demand Forecasting
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered demand analysis and projections from SAP sales data
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-end gap-4">
            {/* Filter type */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Filter By</label>
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setSelectedMaterial(""); setSelectedGroup(""); }}>
                <SelectTrigger className="w-[150px]" data-testid="filter-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="group">Material Group</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Material / Group selector */}
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-muted-foreground">
                {filterType === "material" ? "Material" : "Material Group"}
              </label>
              {filterType === "material" ? (
                <Select value={selectedMaterial} onValueChange={setSelectedMaterial} disabled={loadingMaterials}>
                  <SelectTrigger data-testid="material-select">
                    <SelectValue placeholder={loadingMaterials ? "Loading..." : "Select material..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map(m => (
                      <SelectItem key={m.material} value={m.material}>
                        {m.material} — {m.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={selectedGroup} onValueChange={setSelectedGroup} disabled={loadingMaterials}>
                  <SelectTrigger data-testid="group-select">
                    <SelectValue placeholder={loadingMaterials ? "Loading..." : "Select group..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {materialGroups.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Horizon */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Forecast Horizon</label>
              <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v))}>
                <SelectTrigger className="w-[120px]" data-testid="horizon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={runForecast} disabled={loading} data-testid="run-forecast-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Run Forecast
            </Button>
            {result && (
              <Button
                variant="outline"
                onClick={() => {
                  const params = { horizon_months: horizon };
                  if (filterType === "material") params.material = selectedMaterial;
                  else params.material_group = selectedGroup;
                  downloadExcel("/export/forecast", "forecast.xlsx", { method: "POST", params });
                }}
                data-testid="export-forecast-excel"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard icon={<Package className="w-5 h-5" />} label="Total Demand" value={result.historical?.total_quantity} />
            <KPICard icon={<BarChart3 className="w-5 h-5" />} label="Orders" value={result.historical?.total_records} />
            <KPICard icon={<Users className="w-5 h-5" />} label="Customers" value={result.historical?.unique_customers} />
            <KPICard icon={<Calendar className="w-5 h-5" />} label="Materials" value={result.historical?.unique_materials} />
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  {trendIcon(result.forecast?.trend)}
                  <div>
                    <p className="text-xs text-muted-foreground">Trend</p>
                    <p className="font-semibold text-sm capitalize">{result.forecast?.trend}</p>
                  </div>
                </div>
                <div className="mt-1">{confidenceBadge(result.forecast?.confidence)}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Historical + Forecast Chart */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Demand Trend &amp; Forecast</CardTitle>
                <CardDescription>Historical monthly demand with projected forecast</CardDescription>
              </CardHeader>
              <CardContent>
                <DemandChart
                  historical={result.historical?.monthly || []}
                  forecast={result.forecast?.monthly_forecast || []}
                />
              </CardContent>
            </Card>

            {/* AI Insights */}
            <div className="space-y-4">
              <InsightCard
                title="AI Insights"
                icon={<Lightbulb className="w-4 h-4 text-amber-500" />}
                items={result.forecast?.insights || []}
                color="amber"
              />
              <InsightCard
                title="Risks"
                icon={<ShieldAlert className="w-4 h-4 text-red-500" />}
                items={result.forecast?.risks || []}
                color="red"
              />
              <InsightCard
                title="Recommendations"
                icon={<Lightbulb className="w-4 h-4 text-emerald-500" />}
                items={result.forecast?.recommendations || []}
                color="emerald"
              />
            </div>
          </div>

          {/* Forecast Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Monthly Forecast</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Forecast months */}
                <div>
                  <h4 className="text-sm font-medium mb-3 text-primary">Projected Demand</h4>
                  <div className="space-y-2">
                    {(result.forecast?.monthly_forecast || []).map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border rounded-lg bg-primary/5" data-testid={`forecast-month-${i}`}>
                        <div>
                          <span className="font-medium text-sm">{f.month}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{f.lower_bound}</span>
                          <span className="font-bold text-primary">{f.predicted_quantity}</span>
                          <span className="text-muted-foreground">{f.upper_bound}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top materials breakdown */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Top Materials by Demand</h4>
                  <div className="space-y-2">
                    {(result.historical?.by_material || []).slice(0, 8).map((m, i) => (
                      <div key={i} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                        <div className="truncate flex-1">
                          <span className="font-medium">{m.material}</span>
                          <span className="text-muted-foreground ml-2">{m.description}</span>
                        </div>
                        <div className="font-semibold ml-4">{m.quantity} <span className="text-xs text-muted-foreground">({m.orders} orders)</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!result && !loading && (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <TrendingUp className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Forecast Generated</h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Select a material or material group above and click "Run Forecast" to generate an AI-powered demand projection.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Simple KPI card
const KPICard = ({ icon, label, value }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">{icon}<span className="text-xs">{label}</span></div>
      <p className="text-xl font-bold">{value ?? "-"}</p>
    </CardContent>
  </Card>
);

// Insight/risk/recommendation card
const InsightCard = ({ title, icon, items, color }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">{icon}{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {items.length > 0 ? (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className={`text-xs p-2 rounded border-l-2 border-${color}-400 bg-${color}-50/50`}>
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No {title.toLowerCase()} available</p>
      )}
    </CardContent>
  </Card>
);

// Bar chart using CSS (no external charting library needed)
const DemandChart = ({ historical, forecast }) => {
  const allData = [
    ...historical.map(h => ({ ...h, type: "historical" })),
    ...forecast.map(f => ({ month: f.month, quantity: f.predicted_quantity, lower: f.lower_bound, upper: f.upper_bound, type: "forecast" })),
  ];

  if (allData.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">No data</p>;

  const maxQty = Math.max(...allData.map(d => d.upper || d.quantity || 0), 1);

  return (
    <div className="space-y-1" data-testid="demand-chart">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-primary" />Historical</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-primary/40 border border-primary border-dashed" />Forecast</div>
      </div>

      <div className="space-y-1.5">
        {allData.map((d, i) => {
          const pct = (d.quantity / maxQty) * 100;
          const isForecast = d.type === "forecast";
          const lowerPct = isForecast && d.lower ? (d.lower / maxQty) * 100 : 0;
          const upperPct = isForecast && d.upper ? (d.upper / maxQty) * 100 : 0;

          return (
            <div key={i} className="flex items-center gap-3 group" data-testid={`chart-bar-${d.month}`}>
              <span className="text-xs w-16 text-right text-muted-foreground font-mono">{d.month}</span>
              <div className="flex-1 relative h-6">
                {/* Confidence range for forecast */}
                {isForecast && (
                  <div
                    className="absolute top-0 h-full bg-primary/10 rounded"
                    style={{ left: `${lowerPct}%`, width: `${upperPct - lowerPct}%` }}
                  />
                )}
                {/* Main bar */}
                <div
                  className={`absolute top-0.5 h-5 rounded transition-all ${
                    isForecast
                      ? 'bg-primary/40 border border-primary border-dashed'
                      : 'bg-primary'
                  }`}
                  style={{ width: `${Math.max(pct, 1)}%` }}
                />
              </div>
              <span className="text-xs w-16 font-medium tabular-nums">
                {Math.round(d.quantity)}
                {isForecast && <ArrowUpRight className="w-3 h-3 inline text-primary ml-0.5" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Forecasting;
